/**
 * Relative date filter tokens for QueryBar chips.
 * Must stay in sync with skedyul-core `relativeDateFilter.ts`.
 * Format: `$rel:{amount}:{unit}:{direction}`
 */

export type RelativeDateFilterUnit = 'days' | 'weeks' | 'months'
export type RelativeDateFilterDirection = 'past' | 'future'

export type RelativeDateFilterValue = {
  amount: number
  unit: RelativeDateFilterUnit
  direction: RelativeDateFilterDirection
}

export type RelativeDateFilterPreset = RelativeDateFilterValue & {
  id: string
}

const RELATIVE_DATE_TOKEN_RE =
  /^\$rel:(\d+):(days|weeks|months):(past|future)$/

export function parseRelativeDateFilterValue(
  value: string,
): RelativeDateFilterValue | null {
  const match = RELATIVE_DATE_TOKEN_RE.exec(value)
  if (!match) return null
  const amount = Number(match[1])
  if (!Number.isInteger(amount) || amount < 1) return null
  return {
    amount,
    unit: match[2] as RelativeDateFilterUnit,
    direction: match[3] as RelativeDateFilterDirection,
  }
}

export function isRelativeDateFilterValue(
  value: unknown,
): value is string {
  return typeof value === 'string' && parseRelativeDateFilterValue(value) !== null
}

export function formatRelativeDateFilterValue(
  value: RelativeDateFilterValue,
): string {
  return `$rel:${value.amount}:${value.unit}:${value.direction}`
}

/** v1 presets: past offsets only */
export const RELATIVE_DATE_FILTER_PRESETS: RelativeDateFilterPreset[] = [
  { id: '1-day', amount: 1, unit: 'days', direction: 'past' },
  { id: '1-week', amount: 1, unit: 'weeks', direction: 'past' },
  { id: '2-weeks', amount: 2, unit: 'weeks', direction: 'past' },
  { id: '1-month', amount: 1, unit: 'months', direction: 'past' },
]

function unitPhrase(amount: number, unit: RelativeDateFilterUnit): string {
  if (unit === 'days') return amount === 1 ? 'a day' : `${amount} days`
  if (unit === 'weeks') return amount === 1 ? 'a week' : `${amount} weeks`
  return amount === 1 ? 'a month' : `${amount} months`
}

export function formatRelativeDateFilterLabel(
  value: string | RelativeDateFilterValue,
  op: string,
): string | null {
  const parsed =
    typeof value === 'string' ? parseRelativeDateFilterValue(value) : value
  if (!parsed) return null

  const phrase = unitPhrase(parsed.amount, parsed.unit)
  const isAfter = op === 'gt' || op === 'gte' || op === 'after'
  const isBefore = op === 'lt' || op === 'lte' || op === 'before'

  if (parsed.direction === 'past') {
    if (isAfter) return `within the last ${phrase.replace(/^a /, '')}`
    if (isBefore) return `more than ${phrase} ago`
    return `${phrase} ago`.replace(/^a /, '1 ')
  }

  if (isBefore) return `within the next ${phrase.replace(/^a /, '')}`
  if (isAfter) return `more than ${phrase} from now`
  return `in ${phrase}`
}

export function getRelativeDateFilterPresetsForOp(
  op: string,
): Array<RelativeDateFilterPreset & { label: string; token: string }> {
  return RELATIVE_DATE_FILTER_PRESETS.map((preset) => {
    const token = formatRelativeDateFilterValue(preset)
    const label =
      formatRelativeDateFilterLabel(preset, op) ??
      formatRelativeDateFilterValue(preset)
    return { ...preset, label, token }
  })
}
